import { containsMath, estimateTextBlockHeight, stripMathForPlain } from './math.js';

const GRID = 12;
const SNAP = 8;
export const TEXT_FONT_FAMILY = '"DM Sans", "PingFang SC", sans-serif';

export function snapToGrid(v) {
  return Math.round(v / GRID) * GRID;
}

export function snapPoint(x, y, others, width, height, selfId) {
  let nx = x;
  let ny = y;
  const guides = { v: [], h: [] };
  const edges = {
    left: x,
    right: x + width,
    cx: x + width / 2,
    top: y,
    bottom: y + height,
    cy: y + height / 2,
  };

  const vCandidates = [];
  const hCandidates = [];

  for (const o of others) {
    if (o.id === selfId) continue;
    const b = boundsOf(o);
    const vTargets = [b.left, b.right, b.cx];
    const hTargets = [b.top, b.bottom, b.cy];
    for (const mv of [edges.left, edges.right, edges.cx]) {
      for (const tv of vTargets) {
        const d = Math.abs(mv - tv);
        if (d <= SNAP) {
          vCandidates.push({ d, delta: tv - mv, tv });
        }
      }
    }
    for (const mv of [edges.top, edges.bottom, edges.cy]) {
      for (const tv of hTargets) {
        const d = Math.abs(mv - tv);
        if (d <= SNAP) {
          hCandidates.push({ d, delta: tv - mv, tv });
        }
      }
    }
  }

  if (vCandidates.length) {
    const best = vCandidates.reduce((a, b) => (a.d < b.d ? a : b));
    nx += best.delta;
    guides.v.push(best.tv);
  }
  if (hCandidates.length) {
    const best = hCandidates.reduce((a, b) => (a.d < b.d ? a : b));
    ny += best.delta;
    guides.h.push(best.tv);
  }

  return { x: nx, y: ny, guides };
}

/**
 * 调整大小时对齐：仅吸附正在拖动的边（角点手柄），固定边保持不动。
 */
export function snapResize(x, y, width, height, handle, others, selfId) {
  const guides = { v: [], h: [] };
  let nx = x;
  let ny = y;
  let nw = width;
  let nh = height;

  const findBest = (movingVal, axis) => {
    let best = null;
    for (const o of others) {
      if (o.id === selfId) continue;
      const b = boundsOf(o);
      const targets =
        axis === 'v' ? [b.left, b.right, b.cx] : [b.top, b.bottom, b.cy];
      for (const tv of targets) {
        const d = Math.abs(movingVal - tv);
        if (d <= SNAP && (!best || d < best.d)) {
          best = { d, tv };
        }
      }
    }
    return best;
  };

  if (handle.includes('w')) {
    const best = findBest(nx, 'v');
    if (best) {
      const fixedRight = nx + nw;
      nx = best.tv;
      nw = fixedRight - nx;
      guides.v.push(best.tv);
    }
  } else if (handle.includes('e')) {
    const best = findBest(nx + nw, 'v');
    if (best) {
      nw = best.tv - nx;
      guides.v.push(best.tv);
    }
  }

  if (handle.includes('n')) {
    const best = findBest(ny, 'h');
    if (best) {
      const fixedBottom = ny + nh;
      ny = best.tv;
      nh = fixedBottom - ny;
      guides.h.push(best.tv);
    }
  } else if (handle.includes('s')) {
    const best = findBest(ny + nh, 'h');
    if (best) {
      nh = best.tv - ny;
      guides.h.push(best.tv);
    }
  }

  return { x: nx, y: ny, width: nw, height: nh, guides };
}

export function boundsOf(el) {
  if (el.type === 'freedraw' && el.points?.length) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of el.points) {
      minX = Math.min(minX, p[0]);
      minY = Math.min(minY, p[1]);
      maxX = Math.max(maxX, p[0]);
      maxY = Math.max(maxY, p[1]);
    }
    return { left: minX, top: minY, right: maxX, bottom: maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }
  if (el.type === 'text' && (!el.width || !el.height)) {
    syncTextBounds(el);
  }
  if (el.type === 'arrow' && el.points?.length >= 2) {
    const [a, b] = el.points;
    const minX = Math.min(a[0], b[0]);
    const minY = Math.min(a[1], b[1]);
    const maxX = Math.max(a[0], b[0]);
    const maxY = Math.max(a[1], b[1]);
    return { left: minX, top: minY, right: maxX, bottom: maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }
  const w = el.width || 0;
  const h = el.height || 0;
  return {
    left: el.x,
    top: el.y,
    right: el.x + w,
    bottom: el.y + h,
    cx: el.x + w / 2,
    cy: el.y + h / 2,
  };
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export function hitTestFreedraw(el, wx, wy, radius = 10) {
  if (el.type !== 'freedraw' || !el.points?.length) return false;
  const hitR = radius + (el.strokeWidth || 2) * 1.5;
  const pts = el.points;
  for (let i = 0; i < pts.length; i++) {
    if (Math.hypot(pts[i][0] - wx, pts[i][1] - wy) <= hitR) return true;
    if (i > 0) {
      if (distToSegment(wx, wy, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]) <= hitR) {
        return true;
      }
    }
  }
  return false;
}

export function syncTextBounds(el) {
  if (el.type !== 'text') return;
  const fontSize = el.fontSize || 20;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.font = `${fontSize}px ${TEXT_FONT_FAMILY}`;
  const rawText = el.text || '';
  const lines = rawText.split('\n');
  const hasMath = containsMath(rawText);
  const measureLines = hasMath ? lines.map((line) => stripMathForPlain(line)) : lines;
  let maxW = 0;
  for (const line of measureLines) {
    maxW = Math.max(maxW, ctx.measureText(line || ' ').width);
  }
  el.width = Math.max(24, Math.ceil(maxW) + (hasMath ? Math.ceil(fontSize * 0.4) : 8));
  el.height = estimateTextBlockHeight(rawText, fontSize);
}

export function hitTestRectangle(el, wx, wy, radius = 10) {
  if (!['rectangle', 'ellipse', 'frame', 'card', 'text'].includes(el.type)) return false;
  const { x, y, width, height } = el;
  if (width <= 0 || height <= 0) return false;
  const insideX = wx >= x && wx <= x + width;
  const insideY = wy >= y && wy <= y + height;
  if (insideX && insideY) return true;
  const dx = insideX ? 0 : wx < x ? x - wx : wx - (x + width);
  const dy = insideY ? 0 : wy < y ? y - wy : wy - (y + height);
  return Math.hypot(dx, dy) <= radius;
}

export function hitTestArrow(el, wx, wy, radius = 10) {
  if (el.type !== 'arrow') return false;
  const pts = el.points;
  if (!pts || pts.length < 2) return false;
  const [a, b] = pts;
  if (distToSegment(wx, wy, a[0], a[1], b[0], b[1]) <= radius) return true;
  return Math.hypot(wx - b[0], wy - b[1]) <= radius + 10;
}

/** 橡皮擦命中：手绘 / 矩形 / 箭头（不含卡片） */
export function hitTestEraser(el, wx, wy, radius = 10) {
  if (el.type === 'freedraw') return hitTestFreedraw(el, wx, wy, radius);
  if (
    el.type === 'rectangle' ||
    el.type === 'ellipse' ||
    el.type === 'frame' ||
    el.type === 'text'
  ) {
    return hitTestRectangle(el, wx, wy, radius);
  }
  if (el.type === 'arrow') return hitTestArrow(el, wx, wy, radius);
  return false;
}

export function hitTest(el, wx, wy) {
  if (el.type === 'freedraw') return hitTestFreedraw(el, wx, wy, 8);
  if (el.type === 'arrow') return hitTestArrow(el, wx, wy, 12);
  if (['rectangle', 'ellipse', 'frame', 'card', 'text'].includes(el.type)) {
    return hitTestRectangle(el, wx, wy, 0);
  }
  const b = boundsOf(el);
  return wx >= b.left && wx <= b.right && wy >= b.top && wy <= b.bottom;
}

/** 拖动吸附时忽略箭头/手绘（其 bounds 会跨越大片区域） */
export function isSnapTarget(el) {
  return el?.type && el.type !== 'arrow' && el.type !== 'freedraw';
}

/** 命中点处面积最小的画框（嵌套时优先选中内层小画框） */
export function pickFrameAt(elements, wx, wy) {
  let best = null;
  let bestArea = Infinity;
  for (const el of elements) {
    if (el.type !== 'frame') continue;
    if (!hitTest(el, wx, wy)) continue;
    const area = (el.width || 0) * (el.height || 0);
    if (area > 0 && area < bestArea) {
      bestArea = area;
      best = el;
    }
  }
  return best;
}

/** 按 elements[] 自顶向下命中（末尾在上）；画框在命中时优先更小的嵌套框 */
export function pickElementAt(elements, wx, wy) {
  const list = elements || [];
  for (let i = list.length - 1; i >= 0; i--) {
    const el = list[i];
    if (!el?.type) continue;
    if (el.type === 'frame') {
      if (!hitTest(el, wx, wy)) continue;
      // 同点多个画框时取面积最小者（嵌套内层）
      const frame = pickFrameAt(list, wx, wy);
      return frame || el;
    }
    if (hitTest(el, wx, wy)) return el;
  }
  return null;
}

/** 选择工具：自顶向下优先命中媒体卡（便于从手绘下选中）；媒体之间按 Z 序 */
export function pickElementForSelect(elements, wx, wy) {
  const list = elements || [];
  for (let i = list.length - 1; i >= 0; i--) {
    const el = list[i];
    if (el?.type !== 'card') continue;
    if (el.cardType !== 'image' && el.cardType !== 'file' && el.cardType !== 'webpage') continue;
    if (hitTest(el, wx, wy)) return el;
  }
  return pickElementAt(list, wx, wy);
}

const RESIZABLE_TYPES = new Set(['card', 'rectangle', 'ellipse', 'frame']);
/** 角点命中半径（屏幕像素），换算到世界坐标后随缩放放大可点区域 */
const RESIZE_HANDLE_HIT_PX = 18;

export function resizeHandleCursor(handle) {
  if (!handle) return '';
  return (
    {
      nw: 'nwse-resize',
      se: 'nwse-resize',
      ne: 'nesw-resize',
      sw: 'nesw-resize',
    }[handle] || ''
  );
}

export function hitTestResizeHandle(el, wx, wy, zoom = 1) {
  if (!el || !RESIZABLE_TYPES.has(el.type)) return null;
  const w = el.width || 0;
  const h = el.height || 0;
  if (w <= 0 || h <= 0) return null;
  const hitR = RESIZE_HANDLE_HIT_PX / Math.max(zoom, 0.1);
  const handles = {
    nw: [el.x, el.y],
    ne: [el.x + w, el.y],
    sw: [el.x, el.y + h],
    se: [el.x + w, el.y + h],
  };
  for (const [name, [hx, hy]] of Object.entries(handles)) {
    if (Math.hypot(wx - hx, wy - hy) <= hitR) return name;
  }
  return null;
}

export function isResizableElement(el) {
  return !!el && RESIZABLE_TYPES.has(el.type);
}
